import { Switch } from '@workspace/ui/components/switch'
import { useSetStealthChoiceMutation } from '../../../hooks/api'

interface InvestorVisibilitySectionProps {
  startupId: string
  isStealth: boolean
  stealthChoiceMadeAt: Date | string | null
}

function formatDate(date: Date | string | null): string | null {
  if (!date) return null
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function InvestorVisibilitySection({
  startupId,
  isStealth,
  stealthChoiceMadeAt,
}: InvestorVisibilitySectionProps) {
  const mutation = useSetStealthChoiceMutation(startupId)
  const isVisible = !isStealth
  const lastUpdated = formatDate(stealthChoiceMadeAt)

  const handleToggle = (checked: boolean) => {
    mutation.mutate({ path: { startupId }, body: { isStealth: !checked } })
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium text-foreground">Investor visibility</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Decide whether investors can discover your startup in our deal-flow. Switch this off
          anytime to go back into stealth. No other changes are made.
        </p>
      </div>

      <div className="flex items-start justify-between gap-6 rounded-lg border border-border bg-card p-4">
        <div className="flex-1 space-y-1.5">
          <label htmlFor="visibility-switch" className="font-medium text-sm text-foreground">
            {isVisible ? 'Visible to investors' : 'Stealth, hidden from investors'}
          </label>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {isVisible
              ? 'Investors whose sector, stage and geography preferences match your startup will see you in their personalised deal-flow. They can request an evaluation, watch your pitch and reach out. Your contact details stay private until you connect.'
              : "Your startup is invisible in investor deal-flow and won't appear in match emails or digests. Members of your workspace can still see and edit everything. Pitches you record won't be surfaced to investors until you flip this on."}
          </p>
          {lastUpdated && (
            <p className="pt-1 text-xs text-muted-foreground/70">Last updated: {lastUpdated}</p>
          )}
        </div>
        <Switch
          id="visibility-switch"
          checked={isVisible}
          onCheckedChange={handleToggle}
          disabled={mutation.isPending}
        />
      </div>
    </div>
  )
}
